import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mic, Play, Loader2, Swords, Clock, Sparkles } from "lucide-react";

export function LiveCommentary({ tournamentId, match }) {
  const { t } = useTranslation();
  const [commentary, setCommentary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [animatingIndex, setAnimatingIndex] = useState(-1);

  const fetchCommentary = async () => {
    if (!tournamentId || !match?.match_id) return;
    setLoading(true);
    setCommentary(null);
    setAnimatingIndex(-1);

    try {
      const res = await axios.post(
        `${API}/tournaments/${tournamentId}/matches/${match.match_id}/commentary`
      );
      setCommentary(res.data.commentary);
      // Start reveal animation
      animateMoments(res.data.commentary.moments?.length || 0);
    } catch (err) {
      toast.error("Failed to generate commentary");
    } finally {
      setLoading(false);
    }
  };

  const animateMoments = (count) => {
    let idx = 0;
    const interval = setInterval(() => {
      setAnimatingIndex(idx);
      idx++;
      if (idx >= count) clearInterval(interval);
    }, 1200);
  };

  if (!match?.team1 || !match?.team2) {
    return (
      <Card className="border-dashed border-slate-300">
        <CardContent className="py-8 text-center">
          <Mic className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">{t("tournament.matchNotReady")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="live-commentary">
      {/* Match header */}
      <Card className="border-sky-200 bg-gradient-to-r from-sky-50 to-white">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="font-heading font-bold text-lg text-slate-900">{match.team1.team_name}</p>
                {match.team1.score != null && (
                  <p className="text-2xl font-mono font-bold text-sky-600">{match.team1.score}</p>
                )}
              </div>
              <Swords className="w-6 h-6 text-slate-400" />
              <div className="text-center">
                <p className="font-heading font-bold text-lg text-slate-900">{match.team2.team_name}</p>
                {match.team2.score != null && (
                  <p className="text-2xl font-mono font-bold text-sky-600">{match.team2.score}</p>
                )}
              </div>
            </div>
            <Button
              onClick={fetchCommentary}
              disabled={loading}
              className="gap-2"
              data-testid="generate-commentary-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              {loading ? t("tournament.generating") : t("tournament.generateCommentary")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Commentary display */}
      {loading && (
        <div className="flex flex-col items-center gap-3 py-8" data-testid="commentary-loading">
          <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
          <p className="text-sm text-slate-600">{t("tournament.commentatorPreparing")}</p>
        </div>
      )}

      {commentary && !loading && (
        <div className="space-y-4">
          {/* Intro */}
          <Card className="bg-slate-900 text-white border-none">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Mic className="w-5 h-5 text-sky-400 mt-0.5 shrink-0" />
                <p className="text-sm leading-relaxed">{commentary.intro}</p>
              </div>
            </CardContent>
          </Card>

          {/* Timeline moments */}
          <div className="relative pl-8 space-y-4">
            <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-slate-200" />
            {commentary.moments?.map((moment, i) => (
              <div
                key={i}
                className={`relative transition-all duration-500 ${
                  i <= animatingIndex ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
                }`}
                data-testid={`commentary-moment-${i}`}
              >
                <div className="absolute -left-5 w-3 h-3 rounded-full bg-sky-500 border-2 border-white shadow" />
                <Card>
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-3 h-3 text-sky-500" />
                      <span className="text-xs font-mono font-medium text-sky-600">{moment.time}</span>
                    </div>
                    <p className="text-sm text-slate-700">{moment.commentary}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {/* Climax */}
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-amber-600 uppercase mb-1">{t("tournament.decisiveMoment")}</p>
                  <p className="text-sm text-slate-800">{commentary.climax}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conclusion */}
          <Card className="bg-slate-900 text-white border-none">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Mic className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-sm leading-relaxed">{commentary.conclusion}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
